/**
 * 认证状态管理
 * 使用 Zustand 管理认证状态
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/shared/services/auth';
import type { User } from '@/shared/types/user';
import type { LoginRequest, AuthStore } from '@/modules/auth/types/auth';

/**
 * 认证 Store
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 状态
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // 登录
      login: async (credentials: LoginRequest): Promise<boolean> => {
        console.log('🔐 [AuthStore] login 开始:', { email: credentials.email })
        
        // 🔑 重要：登录前先清除旧的用户状态，确保不会使用旧用户的信息
        set({ user: null, isAuthenticated: false, isLoading: true, error: null });
        console.log('🧹 [AuthStore] 已清除旧的用户状态')
        
        try {
        const result = await authService.login(credentials);
          console.log('📥 [AuthStore] authService.login 返回:', result)
        
        if (result.success && result.user) {
            console.log('✅ [AuthStore] 登录成功，设置用户状态')
          set({
            user: result.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          return true;
        }
        
          console.error('❌ [AuthStore] 登录失败:', result.error)
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: result.error || '登录失败',
        });
        return false;
        } catch (error) {
          console.error('❌ [AuthStore] login 异常:', error)
          const errorMessage = error instanceof Error ? error.message : '登录失败'
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          });
          return false;
        }
      },

      // 登出
      logout: async (): Promise<void> => {
        set({ isLoading: true });
        
        await authService.logout();
        
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      // 刷新 Token
      refreshToken: async (): Promise<boolean> => {
        const success = await authService.refreshToken();
        
        if (!success) {
          set({
            user: null,
            isAuthenticated: false,
            error: null,
          });
        }
        
        return success;
      },

      // 检查认证状态
      checkAuth: async (): Promise<boolean> => {
        const { isAuthenticated, user } = get();
        
        // 如果已经有用户信息，直接返回
        if (isAuthenticated && user) {
          return true;
        }
        
        set({ isLoading: true });
        
        const result = await authService.checkAuth();
        
        set({
          user: result.user,
          isAuthenticated: result.isAuthenticated,
          isLoading: false,
        });
        
        return result.isAuthenticated;
      },

      // 清除错误
      clearError: () => {
        set({ error: null });
      },

      // 设置用户
      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },
    }),
    {
      name: 'axiom-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// 导出类型
export type { AuthStore };

